// ... existing code ...
/**
 * TicketTable component: ticket list table with filters, paging controls,
 * auto-mode actions, and a show-closed toggle for the supervisor UI.
 * UI elements: table, filter menu, action menu, page size selector, pagination.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  Popper,
  ClickAwayListener,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Badge,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Collapse,
  TextField,
} from "@mui/material";
import SupervisorApi from "../apis/supervisorApi";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Ticket } from "types/supervisorType";
import { timeAgo } from "../utils/dateUtils";
import StyledPagination from "./StyledPagination";
import UserAvatar from "./UserAvatar";
import FilterListIcon from "../assets/img/mage_filter.svg";
import CustomSwitch from "./CustomSwitch";
import NewTicketDialog from "./NewTicketDialog";
import AddIcon from "@mui/icons-material/Add";
import MenuItem from "@mui/material/MenuItem";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import {
  OVERLAY_SAFE_POPUP_Z_INDEX,
  withOverlaySafeMenuProps,
} from "../utils/overlayMenu";
import LayeredAutocompleteSelect from "./LayeredAutocompleteSelect";
import { openSupervisorTicketPopupWindow } from "../utils/supervisorTicketPopup";
import { serviceDeskHeading } from "../navigation/workScopes";
import { defaultOpenStatusSelection } from "../utils/ticketStatusFilter";
import { NEUTRAL, ORANGE, PLUM, VIOLET } from "../styles/brandTokens";

const headerCellSx = {
  textAlign: "left",
  whiteSpace: "nowrap",
  fontFamily: "var(--theo-font-sans)",
  fontSize: "12px",
  fontWeight: 600,
  color: NEUTRAL.textMuted,
  cursor: "pointer",
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface FilterState {
  groupName?: string;
  status?: string[];
  customer?: string[];
}

interface TicketTableProps {
  title: string;
  data: Ticket[];
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (event: React.ChangeEvent<unknown>, value: number) => void;
  onRowClick?: (ticketId: string) => void;
  onFilterChange?: (filters: FilterState) => void;
  filters?: FilterState;
  onSortChange?: (sort: { attribute: string; order: "ASC" | "DESC" }[]) => void;
  sort?: { attribute: string; order: "ASC" | "DESC" }[];
  showControls?: boolean;
  autoMode?: boolean;
  onAutoModeChange?: (enabled: boolean) => void;
  autoModeDisabled?: boolean;
  isCreatingThreads?: boolean;
  onPageSizeChange?: (pageSize: number) => void;
  mspType?: string;
  statusOptions?: string[];
  onRefresh?: () => void;
  showClosedSection?: boolean;
  showNewTicketButton?: boolean;
  newTicketQueueName?: string;
  newTicketCategoryName?: string;
  buildTicketDetailPath?: (ticketId: string) => string;
  /** Row / links open ticket in a `window.open` popup (supervisor queues). */
  openTicketDetailInPopup?: boolean;
}

const TicketTable: React.FC<TicketTableProps> = ({
  title,
  data,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onRowClick,
  onFilterChange,
  filters = {
    groupName: "",
    status: [],
    customer: [],
  },
  onSortChange,
  sort = [],
  showControls = true,
  autoMode = false,
  onAutoModeChange,
  autoModeDisabled = false,
  isCreatingThreads = false,
  onPageSizeChange,
  mspType,
  statusOptions,
  onRefresh,
  showClosedSection = true,
  showNewTicketButton,
  newTicketQueueName,
  newTicketCategoryName,
  buildTicketDetailPath,
  openTicketDetailInPopup = false,
}) => {
  const shouldShowNewTicketButton =
    showNewTicketButton ?? Boolean(onAutoModeChange);
  const getTicketDetailPath = (ticketId: string) =>
    buildTicketDetailPath
      ? buildTicketDetailPath(ticketId)
      : `/supervisor/tickets/${ticketId}`;

  const openTicketPopup = (ticketId: string) => {
    openSupervisorTicketPopupWindow(getTicketDetailPath(ticketId));
  };

  const statusFilterOptions = useMemo(() => {
    const normalizedStatusOptions = Array.isArray(statusOptions)
      ? statusOptions
          .map((status) => String(status || "").trim())
          .filter((status) => Boolean(status))
      : [];
    // Fall back to observed ticket values instead of a hardcoded default list.
    const ticketDerivedStatuses = Array.from(
      new Set(
        (data || [])
          .map((ticket) => String(ticket?.status || "").trim())
          .filter((status) => Boolean(status))
      )
    );
    const platformSpecificStatuses =
      normalizedStatusOptions.length > 0 ? normalizedStatusOptions : ticketDerivedStatuses;
    const selectedStatuses = Array.isArray(filters.status) ? filters.status : [];
    return Array.from(new Set([...platformSpecificStatuses, ...selectedStatuses]));
  }, [statusOptions, data, filters.status]);

  const getCustomerDisplayName = (ticket: Ticket): string => {
    const t = ticket as any;
    return (
      String(
        t?.client?.name ||
          t?.company?.name ||
          t?.site?.name ||
          t?.requester?.name ||
          "-"
      ).trim() || "-"
    );
  };

  // Handle sorting when a column header is clicked
  const handleSort = (column: keyof Ticket) => {
    let order: "ASC" | "DESC" = "ASC";
    if (sort.length && sort[0].attribute === column) {
      order = sort[0].order === "ASC" ? "DESC" : "ASC";
    }
    if (onSortChange) {
      onSortChange([{ attribute: column, order }]);
    }
  };

  // Render sort indicator
  const renderSortIndicator = (column: keyof Ticket) => {
    if (sort.length && sort[0].attribute === column) {
      return sort[0].order === "ASC" ? (
        <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
      ) : (
        <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
      );
    }
    return null;
  };

  // State for filter dropdown
  const [filterMenuAnchor, setFilterMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const [activeFilterTab, setActiveFilterTab] = useState(0); // 0 = Status, 1 = Customer
  const [filterSearch, setFilterSearch] = useState("");
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [newTicketDialogOpen, setNewTicketDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [ticketToClose, setTicketToClose] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [closedTicketsExpanded, setClosedTicketsExpanded] = useState(false);
  const [pendingStatusValues, setPendingStatusValues] = useState<string[]>([]);
  const [pendingCustomerValues, setPendingCustomerValues] = useState<string[]>([]);

  const allCustomerIds = useMemo(() => clients.map((client) => client.id), [clients]);
  const normalizedFilterSearch = filterSearch.trim().toLowerCase();
  const visibleFilterStatuses = statusFilterOptions.filter(status => status.toLowerCase().includes(normalizedFilterSearch));
  const visibleFilterCustomers = clients.filter(client => `${client.name} ${client.id}`.toLowerCase().includes(normalizedFilterSearch));
  const selectedStatusValues = useMemo(() => {
    if (!Array.isArray(filters.status)) {
      return defaultOpenStatusSelection(statusFilterOptions, mspType);
    }
    return filters.status;
  }, [filters.status, statusFilterOptions, mspType]);

  const emptyStateMessage = useMemo(() => {
    const activeStatuses = Array.isArray(filters.status)
      ? filters.status.map((status) => String(status).trim()).filter(Boolean)
      : [];

    if (activeStatuses.length === 0) {
      return "No tickets found";
    }

    const statusDescriptor = activeStatuses.length === 1 ? "status" : "statuses";
    return `No tickets found for ${statusDescriptor} ${activeStatuses.join(", ")}. Please select another status`;
  }, [filters.status]);
  const selectedCustomerValues = useMemo(() => {
    if (filters.customer === undefined) {
      return allCustomerIds;
    }
    return Array.isArray(filters.customer) ? filters.customer : [];
  }, [filters.customer, allCustomerIds]);

  useEffect(() => {
    setPendingStatusValues(selectedStatusValues);
  }, [selectedStatusValues]);

  useEffect(() => {
    setPendingCustomerValues(selectedCustomerValues);
  }, [selectedCustomerValues]);

  const heading = useMemo(() => {
    const customerNames = Array.isArray(filters.customer)
      ? filters.customer.map(
          (id) => clients.find((client) => client.id === id)?.name || String(id)
        )
      : undefined;
    return serviceDeskHeading(title, filters.status, customerNames);
  }, [title, filters.status, filters.customer, clients]);

  const supervisorApi = useMemo(() => new SupervisorApi(), []);

  const { activeTickets, closedTickets } = useMemo(() => {
    if (!showClosedSection) {
      return {
        activeTickets: data || [],
        closedTickets: [],
      };
    }

    const active: Ticket[] = [];
    const closed: Ticket[] = [];

    data?.forEach((ticket) => {
      const status = String(ticket.status || "").toLowerCase();
      const isClosed =
        status.includes("complete") ||
        status.includes("closed") ||
        status.includes("resolved") ||
        status.includes("approved") ||
        status.includes("denied");

      if (isClosed) {
        closed.push(ticket);
      } else {
        active.push(ticket);
      }
    });
    return { activeTickets: active, closedTickets: closed };
  }, [data, showClosedSection]);

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        const response = await supervisorApi.getClientsList();
        if (Array.isArray(response)) {
          setClients(response);
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        setLoadingClients(false);
      }
    };
    fetchClients();
  }, [supervisorApi]);

  const handleFilterIconClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterMenuAnchor((currentAnchor) =>
      currentAnchor ? null : event.currentTarget
    );
  };

  const handleFilterToggle = (filterType: 'status' | 'customer', value: string) => {
    const allFilterValues = filterType === "status" ? statusFilterOptions : allCustomerIds;
    if (allFilterValues.length === 0) {
      return;
    }

    const currentFilters =
      filterType === "status" ? [...pendingStatusValues] : [...pendingCustomerValues];
    const nextSelection = currentFilters.includes(value)
      ? currentFilters.filter((f) => f !== value)
      : [...currentFilters, value];
    const normalizedSelection = allFilterValues.filter((candidate) =>
      nextSelection.includes(candidate)
    );
    if (filterType === "status") {
      setPendingStatusValues(normalizedSelection);
    } else {
      setPendingCustomerValues(normalizedSelection);
    }
  };

  const handleApplyFilters = () => {
    const openByDefault = defaultOpenStatusSelection(statusFilterOptions, mspType);
    const sameAsOpenDefault =
      pendingStatusValues.length === openByDefault.length &&
      pendingStatusValues.every((status) => openByDefault.includes(status));
    const normalizedStatusSelection = sameAsOpenDefault ? undefined : pendingStatusValues;
    const normalizedCustomerSelection =
      loadingClients ||
      allCustomerIds.length === 0 ||
      pendingCustomerValues.length === allCustomerIds.length
        ? undefined
        : pendingCustomerValues;

    if (onFilterChange) {
      onFilterChange({
        ...filters,
        status: normalizedStatusSelection,
        customer: normalizedCustomerSelection,
      });
    }
    handleMenuClose();
  };

  const handleMenuClose = () => {
    setFilterMenuAnchor(null);
    setFilterSearch("");
  };

  const getActiveFilterCount = () => {
    const statusCount = selectedStatusValues.length;
    const customerCount = selectedCustomerValues.length;
    return statusCount + customerCount;
  };

  const handleActionMenuClick = (ticketId: string, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent row click
    setSelectedTicketId(ticketId);
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
  };

  const handleCloseClick = () => {
    if (selectedTicketId) {
      setTicketToClose(selectedTicketId);
      setCloseDialogOpen(true);
      handleActionMenuClose();
    }
  };

  const handleCloseConfirm = async () => {
    if (!ticketToClose) return;

    setIsClosing(true);
    try {
      await supervisorApi.closeTicket(ticketToClose);
      setCloseDialogOpen(false);
      setTicketToClose(null);
      onRefresh?.();
    } catch (error: any) {
      console.error("Error closing ticket:", error);
      alert(error.response?.data?.error || "Failed to close ticket");
    } finally {
      setIsClosing(false);
    }
  };

  const handleCloseCancel = () => {
    setCloseDialogOpen(false);
    setTicketToClose(null);
  };

  // Helper function to render table rows
  const renderTicketRows = (tickets: Ticket[]) => {
    if (!tickets || tickets.length === 0) {
      return (
        <TableRow>
          <TableCell
            colSpan={8}
            sx={{
              textAlign: "center",
              py: 4,
              fontFamily: "var(--theo-font-sans)",
              fontSize: "14px",
              color: NEUTRAL.textSecondary,
            }}
          >
            {emptyStateMessage}
          </TableCell>
        </TableRow>
      );
    }

    const rowClickable = Boolean(onRowClick) || openTicketDetailInPopup;

    return tickets.map((row) => (
      <TableRow
        key={row.id}
        hover
        sx={{
          fontFamily: "var(--theo-font-sans)",
          cursor: rowClickable ? "pointer" : "default",
          "&:last-child td, &:last-child th": { border: 0 },
          "&:hover td, &:hover th": {
            backgroundColor: NEUTRAL.hover,
            paddingLeft: "16px",
            paddingRight: "16px",
          },
        }}
        onClick={(event) => {
          if (openTicketDetailInPopup) {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
              return;
            }
            openTicketPopup(row.id);
            return;
          }
          if (!onRowClick) return;
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
          }
          onRowClick(row.id);
        }}
      >
        <TableCell
          component="th"
          scope="row"
          sx={{
            fontSize: "12.5px",
            fontWeight: 500,
            /* tabular-nums keeps the ID column aligned for scanning without a
               second typeface; monospace read as clunky beside the sans issue text. */
            fontVariantNumeric: "tabular-nums",
            color: NEUTRAL.text,
            textAlign: "left",
            whiteSpace: "nowrap",
          }}
        >
          {openTicketDetailInPopup ? (
            <a
              href={getTicketDetailPath(row.id)}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openTicketPopup(row.id);
              }}
              style={{
                color: "inherit",
                textDecoration: "none",
                display: "inline-block",
                width: "100%",
                cursor: "pointer",
              }}
            >
              {row.displayId || row.id}
            </a>
          ) : (
            <RouterLink
              to={getTicketDetailPath(row.id)}
              onClick={(event) => event.stopPropagation()}
              style={{
                color: "inherit",
                textDecoration: "none",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.displayId || row.id}
            </RouterLink>
          )}
        </TableCell>
        <TableCell
          sx={{
            paddingY: "15px",
            fontFamily: "var(--theo-font-sans)",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: 1.45,
            color: NEUTRAL.text,
            textAlign: "left",
            minWidth: 280,
          }}
        >
          {openTicketDetailInPopup ? (
            <a
              href={getTicketDetailPath(row.id)}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openTicketPopup(row.id);
              }}
              style={{
                color: "inherit",
                textDecoration: "none",
                display: "inline-block",
                width: "100%",
                cursor: "pointer",
              }}
            >
              {row.title}
            </a>
          ) : (
            <RouterLink
              to={getTicketDetailPath(row.id)}
              onClick={(event) => event.stopPropagation()}
              style={{
                color: "inherit",
                textDecoration: "none",
                display: "inline-block",
                width: "100%",
              }}
            >
              {row.title}
            </RouterLink>
          )}
        </TableCell>
        <TableCell
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: "14px",
            fontWeight: 500,
            color: NEUTRAL.textSecondary,
            textAlign: "left",
            whiteSpace: "nowrap",
          }}
          title={row.created_at ? new Date(row.created_at).toLocaleString() : undefined}
        >
          {/* "4 months ago" is prose, not a machine-shaped value; monospace made the
              column read as clunky next to the sans issue text. The ID column keeps mono. */}
          <Box component="span" sx={{ fontSize: "12.5px", fontVariantNumeric: "tabular-nums" }}>
            {timeAgo(row.created_at)}
          </Box>
        </TableCell>
        <TableCell
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: "14px",
            fontWeight: 500,
            color: NEUTRAL.text,
            textAlign: "left",
          }}
        >
          {getCustomerDisplayName(row)}
        </TableCell>
        <TableCell
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: "14px",
            fontWeight: 400,
            color: NEUTRAL.text,
            textAlign: "left",
          }}
        >
          {row.assigned_to ? (
            <UserAvatar
              name={row.assigned_to.name}
              avatarUrl={row.assigned_to.avatarUrl}
              showName={true}
            />
          ) : (
            "-"
          )}
        </TableCell>
        <TableCell
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: "14px",
            fontWeight: 500,
            color: NEUTRAL.textSecondary,
            textAlign: "left",
            whiteSpace: "nowrap",
          }}
        >
          {row.status
            ? String(row.status).charAt(0).toUpperCase() +
              String(row.status).slice(1)
            : "-"}
        </TableCell>
        <TableCell
          onClick={(e) => {
            e.stopPropagation();
            if (openTicketDetailInPopup) {
              openTicketPopup(row.id);
            } else if (onRowClick) {
              onRowClick(row.id);
            }
          }}
          sx={{ cursor: "pointer", textAlign: "left" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "flex-start",
            }}
          >
            {row.aiThread === true ? (
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: "14px",
                  fontWeight: 700,
                  color: VIOLET.theoText,
                  textAlign: "left",
                }}
              >
                Theo
                {row.isAutoResolved && (
                  <Typography
                    component="sup"
                    sx={{
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: "10px",
                      fontWeight: 500,
                      color: VIOLET.theoText,
                      marginLeft: "2px",
                    }}
                  >
                    auto
                  </Typography>
                )}
              </Typography>
            ) : (
              "-"
            )}
          </Box>
        </TableCell>
        <TableCell
          sx={{ textAlign: "center" }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            size="small"
            onClick={(e) => handleActionMenuClick(row.id, e)}
            sx={{
              color: NEUTRAL.textSecondary,
              "&:hover": {
                color: NEUTRAL.text,
                backgroundColor: NEUTRAL.hover,
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <>
      <Box
        sx={{
          flex: "1 1 auto",
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "white",
          paddingTop: 1,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Title and Controls */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
            height: 48,
            ml: 2,
            mr: 1,
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0, flex: 1, pr: 2 }}>
            <Typography
              variant="h6"
              noWrap
              title={heading}
              sx={{
                fontWeight: 700,
                fontFamily: "var(--theo-font-sans)",
                letterSpacing: "-0.01em",
                color: NEUTRAL.text,
                mb: 0,
              }}
            >
              {heading}
            </Typography>
          </Box>
          {/* Controls - Always visible, even when there are no tickets */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {shouldShowNewTicketButton && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setNewTicketDialogOpen(true)}
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  textTransform: "none",
                  backgroundColor: VIOLET.main,
                  color: "white",
                  mr: 2,
                  "&:hover": {
                    backgroundColor: VIOLET.dark,
                  },
                }}
              >
                New Ticket
              </Button>
            )}
            {onRefresh && (shouldShowNewTicketButton || onAutoModeChange) && (
              <IconButton
                onClick={onRefresh}
                disabled={isCreatingThreads}
                size="small"
                sx={{
                  mr: 2,
                  color: NEUTRAL.text,
                  border: `1px solid ${NEUTRAL.borderStrong}`,
                  borderRadius: 1,
                  width: 32,
                  height: 32,
                  "&:hover": {
                    backgroundColor: "rgba(90, 94, 115, 0.08)",
                  },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            )}
            {onAutoModeChange && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: NEUTRAL.text,
                      fontSize: "0.875rem",
                    }}
                  >
                    Auto-mode
                  </Typography>
                  <CustomSwitch
                    checked={autoMode}
                    onChange={(e) => onAutoModeChange(e.target.checked)}
                    disabled={isCreatingThreads || autoModeDisabled}
                  />
                </Box>
              </>
            )}
              {/* Filter Icon with Dropdown */}
              <ClickAwayListener onClickAway={handleMenuClose}>
                <Box sx={{ position: "relative", mr: 1 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${NEUTRAL.border}`,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      p: 0.5,
                    }}
                  >
                    <Badge
                      badgeContent={getActiveFilterCount()}
                      sx={{
                        "& .MuiBadge-badge": { bgcolor: ORANGE.main, color: PLUM.rail, fontWeight: 700 },
                      }}
                      invisible={getActiveFilterCount() === 0}
                    >
                      <IconButton size="small" onClick={handleFilterIconClick}>
                        <img src={FilterListIcon} alt="Filter" />
                      </IconButton>
                    </Badge>
                  </Paper>
                  <Popper
                    open={Boolean(filterMenuAnchor)}
                    anchorEl={filterMenuAnchor}
                    placement="bottom-end"
                    sx={{ zIndex: OVERLAY_SAFE_POPUP_Z_INDEX }}
                    modifiers={[
                      {
                        name: "offset",
                        options: { offset: [0, 8] },
                      },
                      {
                        name: "preventOverflow",
                        options: { padding: 8 },
                      },
                    ]}
                  >
                    <Paper
                      elevation={3}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          handleMenuClose();
                        }
                      }}
                      sx={{
                        p: 0,
                        minWidth: 280,
                        maxHeight: 500,
                        overflow: "auto",
                      }}
                    >
                      {/* Tabs for different filter types */}
                      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <Tabs
                          value={activeFilterTab}
                          onChange={(e, newValue) => { setActiveFilterTab(newValue); setFilterSearch(""); }}
                          sx={{
                            "& .MuiTab-root": {
                              fontFamily: "var(--theo-font-sans)",
                              fontSize: "14px",
                              textTransform: "none",
                              minHeight: 48,
                            },
                          }}
                        >
                          <Tab
                            label={`Status${pendingStatusValues.length > 0 ? ` (${pendingStatusValues.length})` : ""}`}
                          />
                          <Tab
                            label={`Customer${pendingCustomerValues.length > 0 ? ` (${pendingCustomerValues.length})` : ""}`}
                          />
                        </Tabs>
                      </Box>

                      {/* Filter Content */}
                      <Box sx={{ p: 2, maxHeight: 400, overflow: "auto" }}>
                        <TextField
                          size="small"
                          fullWidth
                          label={activeFilterTab === 0 ? "Search statuses" : "Search customers"}
                          value={filterSearch}
                          onChange={event => setFilterSearch(event.target.value)}
                          sx={{ mb: 1.5 }}
                        />
                        {/* Status Filter Tab */}
                        {activeFilterTab === 0 && (
                          <Box>
                            <Box
                              sx={{
                                mb: 1.5,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: NEUTRAL.text,
                                  fontWeight: 500,
                                  fontSize: "14px",
                                  fontFamily: "var(--theo-font-sans)",
                                }}
                              >
                                Filter by Status
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Button
                                  size="small"
                                  onClick={() => setPendingStatusValues([])}
                                  disabled={pendingStatusValues.length === 0}
                                  sx={{
                                    minWidth: "auto",
                                    p: 0,
                                    textTransform: "none",
                                    fontFamily: "var(--theo-font-sans)",
                                    fontSize: "12px",
                                    color: VIOLET.main,
                                  }}
                                >
                                  Unselect all
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => setPendingStatusValues([...statusFilterOptions])}
                                  disabled={statusFilterOptions.length === 0}
                                  sx={{
                                    minWidth: "auto",
                                    p: 0,
                                    textTransform: "none",
                                    fontFamily: "var(--theo-font-sans)",
                                    fontSize: "12px",
                                    color: VIOLET.main,
                                  }}
                                >
                                  Select all
                                </Button>
                              </Box>
                            </Box>
                            {!visibleFilterStatuses.length && <Typography variant="body2" color="text.secondary">No statuses match your search.</Typography>}
                            {visibleFilterStatuses.map((status) => (
                              <Box
                                key={status}
                                sx={{
                                  mb: 0.5,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.5,
                                  background: pendingStatusValues.includes(status) ? VIOLET.tint : "transparent",
                                  "&:hover": { background: NEUTRAL.hover },
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={pendingStatusValues.includes(status)}
                                      onChange={() => handleFilterToggle("status", status)}
                                      sx={{
                                        color: NEUTRAL.textMuted,
                                        "&.Mui-checked": {
                                          color: VIOLET.main,
                                        },
                                      }}
                                    />
                                  }
                                  label={status}
                                  sx={{
                                    m: 0,
                                    fontSize: "14px",
                                    fontFamily: "var(--theo-font-sans)",
                                    fontWeight: 500,
                                    color: NEUTRAL.text,
                                    flex: 1,
                                  }}
                                />
                                <Button
                                  size="small"
                                  onClick={() => setPendingStatusValues([status])}
                                  sx={{
                                    minWidth: "auto",
                                    p: 0,
                                    textTransform: "none",
                                    fontFamily: "var(--theo-font-sans)",
                                    fontSize: "12px",
                                    color: VIOLET.main,
                                  }}
                                >
                                  Only
                                </Button>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {/* Customer Filter Tab */}
                        {activeFilterTab === 1 && (
                          <Box>
                            <Box
                              sx={{
                                mb: 1.5,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: NEUTRAL.text,
                                  fontWeight: 500,
                                  fontSize: "14px",
                                  fontFamily: "var(--theo-font-sans)",
                                }}
                              >
                                Filter by Customer
                              </Typography>
                              <Button
                                size="small"
                                onClick={() => setPendingCustomerValues([...allCustomerIds])}
                                disabled={allCustomerIds.length === 0}
                                sx={{
                                  minWidth: "auto",
                                  p: 0,
                                  textTransform: "none",
                                  fontFamily: "var(--theo-font-sans)",
                                  fontSize: "12px",
                                  color: VIOLET.main,
                                }}
                              >
                                Select all
                              </Button>
                            </Box>
                            {loadingClients ? (
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontFamily: "var(--theo-font-sans)",
                                  color: NEUTRAL.textMuted,
                                  textAlign: "center",
                                  py: 2,
                                }}
                              >
                                Loading customers...
                              </Typography>
                            ) : visibleFilterCustomers.length === 0 ? (
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontFamily: "var(--theo-font-sans)",
                                  color: NEUTRAL.textMuted,
                                  textAlign: "center",
                                  py: 2,
                                }}
                              >
                                {filterSearch ? "No customers match your search." : "No customers available"}
                              </Typography>
                            ) : (
                              visibleFilterCustomers.map((client) => (
                                <Box
                                  key={client.id}
                                  sx={{
                                    mb: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.5,
                                    background: pendingCustomerValues.includes(client.id) ? VIOLET.tint : "transparent",
                                    "&:hover": { background: NEUTRAL.hover },
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={pendingCustomerValues.includes(client.id)}
                                        onChange={() => handleFilterToggle("customer", client.id)}
                                        sx={{
                                          color: NEUTRAL.textMuted,
                                          "&.Mui-checked": {
                                            color: VIOLET.main,
                                          },
                                        }}
                                      />
                                    }
                                    label={client.name}
                                    sx={{
                                      m: 0,
                                      fontSize: "14px",
                                      fontFamily: "var(--theo-font-sans)",
                                      fontWeight: 500,
                                      color: NEUTRAL.text,
                                      flex: 1,
                                    }}
                                  />
                                  <Button
                                    size="small"
                                    onClick={() => setPendingCustomerValues([client.id])}
                                    sx={{
                                      minWidth: "auto",
                                      p: 0,
                                      textTransform: "none",
                                      fontFamily: "var(--theo-font-sans)",
                                      fontSize: "12px",
                                      color: VIOLET.main,
                                    }}
                                  >
                                    Only
                                  </Button>
                                </Box>
                              ))
                            )}
                          </Box>
                        )}
                      </Box>
                      <Box
                        sx={{
                          borderTop: `1px solid ${NEUTRAL.border}`,
                          p: 1.5,
                          display: "flex",
                          justifyContent: "flex-end",
                          backgroundColor: "#fff",
                          position: "sticky",
                          bottom: 0,
                        }}
                      >
                        <Button
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={handleApplyFilters}
                          sx={{
                            textTransform: "none",
                            fontFamily: "var(--theo-font-sans)",
                            backgroundColor: VIOLET.main,
                            "&:hover": { backgroundColor: VIOLET.dark },
                          }}
                        >
                          Apply
                        </Button>
                      </Box>
                    </Paper>
                  </Popper>
                </Box>
              </ClickAwayListener>
          </Box>
        </Box>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: `1px solid ${NEUTRAL.border}`,
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: NEUTRAL.subtle, height: 44 }}>
                <TableCell
                  sx={headerCellSx}
                  onClick={() => handleSort("displayId")}
                >
                  ID {renderSortIndicator("displayId")}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  Issue {renderSortIndicator("title")}
                </TableCell>
                <TableCell
                  sx={headerCellSx}
                  onClick={() => handleSort("created_at")}
                >
                  Created {renderSortIndicator("created_at")}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  Customer {renderSortIndicator("requester")}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  Assignee {renderSortIndicator("assigned_to")}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  Status {renderSortIndicator("status")}
                </TableCell>
                <TableCell sx={headerCellSx}>AI Engineer</TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: "center" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderTicketRows(activeTickets)}
            </TableBody>
          </Table>
        </TableContainer>

        {showClosedSection && (
          <Box sx={{ mt: 2 }}>
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${NEUTRAL.border}`,
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => setClosedTicketsExpanded(!closedTicketsExpanded)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  backgroundColor: NEUTRAL.subtle,
                  "&:hover": {
                    backgroundColor: NEUTRAL.hover,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: NEUTRAL.text,
                  }}
                >
                  Closed Tickets ({closedTickets.length})
                </Typography>
                <IconButton size="small">
                  {closedTicketsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={closedTicketsExpanded}>
                <TableContainer
                  sx={{
                    maxHeight: 360,
                    overflowY: "auto",
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: NEUTRAL.subtle, height: 44 }}>
                        <TableCell
                          sx={headerCellSx}
                          onClick={() => handleSort("displayId")}
                        >
                          ID {renderSortIndicator("displayId")}
                        </TableCell>
                        <TableCell sx={headerCellSx}>
                          Issue {renderSortIndicator("title")}
                        </TableCell>
                        <TableCell
                          sx={headerCellSx}
                          onClick={() => handleSort("created_at")}
                        >
                          Created {renderSortIndicator("created_at")}
                        </TableCell>
                        <TableCell sx={headerCellSx}>
                          Customer {renderSortIndicator("requester")}
                        </TableCell>
                        <TableCell sx={headerCellSx}>
                          Assignee {renderSortIndicator("assigned_to")}
                        </TableCell>
                        <TableCell sx={headerCellSx}>
                          Status {renderSortIndicator("status")}
                        </TableCell>
                        <TableCell sx={headerCellSx}>AI Engineer</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: "center" }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {renderTicketRows(closedTickets)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Paper>
          </Box>
        )}
        {data &&
          data.length > 0 &&
          page &&
          pageSize &&
          totalCount &&
          onPageChange && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              flexShrink: 0,
              px: 2,
              py: 1.5,
              gap: 2,
            }}
          >
            <StyledPagination
              count={Math.ceil(totalCount / pageSize)}
              page={page}
              onChange={onPageChange}
            />
            {onPageSizeChange && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LayeredAutocompleteSelect
                  label="Rows"
                  value={String(pageSize)}
                  onChange={(nextValue) => onPageSizeChange(Number(nextValue))}
                  disableClearable
                  options={PAGE_SIZE_OPTIONS.map((option) => ({
                    value: String(option),
                    label: String(option),
                  }))}
                  sx={{ minWidth: 80 }}
                  textFieldSx={{
                    "& .MuiInputBase-root": {
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputLabel-root": {
                      display: "none",
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: NEUTRAL.text,
                    fontSize: "0.875rem",
                    ml: 1,
                  }}
                >
                  per page
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <NewTicketDialog
        open={newTicketDialogOpen}
        onClose={() => setNewTicketDialogOpen(false)}
        queueName={newTicketQueueName || filters.groupName}
        categoryName={newTicketCategoryName}
        onTicketCreated={() => {
          onRefresh?.();
        }}
      />
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        {...withOverlaySafeMenuProps({
          PaperProps: {
            sx: {
              borderRadius: 2,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              minWidth: 150,
            },
          },
        })}
      >
        <MenuItem
          onClick={handleCloseClick}
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: "14px",
            color: "#d32f2f",
            "&:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.08)",
            },
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CloseIcon fontSize="small" />
          Close Ticket
        </MenuItem>
      </Menu>
      <Dialog
        open={closeDialogOpen}
        onClose={handleCloseCancel}
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            fontSize: "20px",
            color: NEUTRAL.text,
          }}
        >
          Close Ticket
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: "14px",
              color: NEUTRAL.text,
            }}
          >
            Are you sure you want to close this ticket? The ticket will be marked as complete.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseCancel}
            disabled={isClosing}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              color: NEUTRAL.text,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCloseConfirm}
            variant="contained"
            disabled={isClosing}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              backgroundColor: "#d32f2f",
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
              "&:disabled": {
                backgroundColor: "#E0E0E0",
              },
            }}
          >
            {isClosing ? <CircularProgress size={24} /> : "Close Ticket"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicketTable;
